import styled from "styled-components";
import { StaggerReveal, MotionItem } from "../../components/ui/Motion";

export const PageHeader = styled.div`
  position: relative;
  padding-top: calc(4rem + 60px);
  padding-bottom: ${({ theme }) => theme.spacing["3xl"]};
  background: ${({ theme }) => theme.colors.navy};
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        circle at 85% 20%,
        rgba(112, 141, 171, 0.18) 0%,
        transparent 45%
      ),
      radial-gradient(
        circle at 10% 90%,
        rgba(201, 169, 110, 0.08) 0%,
        transparent 40%
      );
    pointer-events: none;
  }

  & > * {
    position: relative;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-top: calc(4rem + 50px);
  }
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.5rem, 5vw, ${({ theme }) => theme.fontSizes["6xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
`;

export const HeaderSub = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: rgba(255, 255, 255, 0.55);
  max-width: 480px;
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: ${({ theme }) => theme.spacing["3xl"]};
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing["2xl"]};
  }
`;

export const FormCard = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing["2xl"]};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;

  /* &::before {
    content: "";
    position: absolute;
    bottom: -120px;
    right: -120px;
    width: 360px;
    height: 360px;
    background: radial-gradient(
      circle,
      rgba(112, 141, 171, 0.18) 0%,
      rgba(112, 141, 171, 0.08) 35%,
      transparent 70%
    );
    pointer-events: none;
  } */

  & > * {
    position: relative;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ theme }) => theme.spacing["lg"]};
  }
`;

export const FormTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const FormField = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const Label = styled.label`
  display: block;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  outline: none;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.midGray};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(112, 141, 171, 0.12);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.midGray};
  }
`;

export const SelectWrapper = styled.div`
  position: relative;
`;

export const SelectTrigger = styled.button`
  width: 100%;
  padding: 12px 16px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  border: 1px solid
    ${({ theme, $open }) =>
      $open ? theme.colors.primary : theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  outline: none;
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  color: ${({ theme, $hasValue }) =>
    $hasValue ? theme.colors.textPrimary : theme.colors.midGray};

  &:hover {
    border-color: ${({ theme, $open }) =>
      $open ? theme.colors.primary : theme.colors.midGray};
  }

  ${({ $open }) => $open && `box-shadow: 0 0 0 3px rgba(112, 141, 171, 0.12);`}

  &::after {
    content: "";
    width: 8px;
    height: 8px;
    border-right: 1.5px solid ${({ theme }) => theme.colors.midGray};
    border-bottom: 1.5px solid ${({ theme }) => theme.colors.midGray};
    transform: ${({ $open }) => ($open ? "rotate(-135deg)" : "rotate(45deg)")};
    transition: transform ${({ theme }) => theme.transitions.fast};
    flex-shrink: 0;
    margin-left: ${({ theme }) => theme.spacing.sm};
  }
`;

export const SelectDropdown = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  list-style: none;
  padding: ${({ theme }) => theme.spacing.xs} 0;
  z-index: 10;
  overflow: hidden;
`;

export const SelectOption = styled.li`
  padding: 10px 16px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.pearl : "transparent"};

  &:hover {
    background: ${({ theme }) => theme.colors.pearl};
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-family: ${({ theme }) => theme.fonts.body};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  outline: none;
  resize: vertical;
  min-height: 140px;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.midGray};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(112, 141, 171, 0.12);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.midGray};
  }
`;

export const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: ${({ theme }) => theme.spacing.md};
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(112, 141, 171, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SuccessMsg = styled.div`
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing["3xl"]} ${theme.spacing.xl}`};

  h3 {
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: ${({ theme }) => theme.fontSizes["3xl"]};
    font-weight: ${({ theme }) => theme.fontWeights.light};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: ${({ theme }) => theme.lineHeights.relaxed};
  }
`;

export const InfoSide = styled(StaggerReveal)`
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-left: ${({ theme }) => theme.spacing.lg};
  }
`;

export const InfoCard = styled(MotionItem)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};

  &:first-child {
    padding-top: 0;
  }
`;

export const InfoIcon = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.pearl};
  color: ${({ theme }) => theme.colors.primary};
  transition: all ${({ theme }) => theme.transitions.normal};

  svg {
    width: 20px;
    height: 20px;
  }

  ${InfoCard}:hover & {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const InfoText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const InfoLabel = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const InfoValue = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  word-break: break-word;
`;

export const InfoLink = styled.a`
  color: inherit;
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const HoursList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const HoursRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: ${({ theme }) => theme.lineHeights.normal};
`;

export const HoursDay = styled.span`
  flex-shrink: 0;
  min-width: 88px;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textLight};
`;

export const HoursTime = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
`;

export const HoursClosed = styled.span`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.midGray};
`;

export const SocialBlock = styled(MotionItem)`
  padding-top: ${({ theme }) => theme.spacing.xl};
`;

export const SocialLabel = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const SocialRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const SocialLink = styled.a`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all ${({ theme }) => theme.transitions.normal};

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(112, 141, 171, 0.25);
  }
`;
